import { pageSxFor } from '../components/pageLayout';
import { useState, useEffect } from "react";
import { useTheme } from '@mui/material';
import { Box, Button, useMediaQuery } from "@mui/material";
import {
  AccountTree, AdminPanelSettingsOutlined, BadgeOutlined, CellTower, DownloadOutlined, Flag, ManageAccountsOutlined,
  PeopleAltOutlined, PersonOutline, PersonOutlined, PlaceOutlined, Shield,
} from "@mui/icons-material";
import PersonIcon from '@mui/icons-material/Person';
import FlightIcon from '@mui/icons-material/Flight';
import jsPDF from "jspdf"; 
import autoTable from "jspdf-autotable";

import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import FlexBetween from "../components/FlexBetween";
import StatBox from "../components/StateBox";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import HourStatisticChart from "../components/HourStatisticChart";
import BreakdownChart from "../components/BreakdownChart";
import OnlineStateChart from "../components/OnlineStateChart";

// Legend icons; unit types use the same icons as the Unit page
const ROLE_ICONS = { Root: AdminPanelSettingsOutlined, Admin: ManageAccountsOutlined, User: PersonOutlined, Guest: PersonOutline };
const UNIT_ICONS = { Command: Shield, Regiment: AccountTree, Company: Flag, Outpost: CellTower };

const Dashboard = () => {
  // theme
  const theme = useTheme();
  // is large desktop screen
  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

  const { auth } = useAuthContext()
  const { setTitle } = usePathContext()

  const axiosPrivate = useAxiosPrivate();
  const [dashboardData, setDashboardData] = useState({});

  useEffect(() => {
    setTitle("Dashboard")

    const controller = new AbortController();
    const getDashboardData = async () => {
      try {
        const response = await axiosPrivate.get('/api/dashboard', {
          signal: controller.signal
        });
        setDashboardData(response.data);
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      }
    };

    if (auth) {
      getDashboardData();
    }
    const intervalId = setInterval(getDashboardData, 20000);

    return () => {
      clearInterval(intervalId);
      controller.abort()
    }
  }, [auth, axiosPrivate, setTitle])

  const handleDownloadReports = () => {
    if (!dashboardData || Object.keys(dashboardData).length === 0) {
      console.warn("Dashboard data is not available yet.");
      return;
    }

    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    // --------------------------------------------------
    // Header
    // --------------------------------------------------

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Dashboard Report", pageWidth / 2, 20, {
      align: "center",
    });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const generatedAt = new Date().toLocaleString();

    doc.text(
      `Generated: ${generatedAt}`,
      pageWidth / 2,
      28,
      { align: "center" }
    );

    // --------------------------------------------------
    // Summary
    // --------------------------------------------------

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Dashboard Summary", 14, 42);

    autoTable(doc, {
      startY: 47,
      head: [["Metric", "Value"]],
      body: [
        [
          "Total Registered Users",
          dashboardData.totalRegUsers ?? 0,
        ],
        [
          "Today Logged-in Users",
          dashboardData.todayLoginUsers ?? 0,
        ],
        [
          "Current Online Users",
          dashboardData.currConnectionCount ?? 0,
        ],
        [
          "Strange Users",
          dashboardData.strangeUserCount ?? 0,
        ],
        [
          "Total Detected Flights",
          dashboardData.totalFlights ?? 0,
        ],
        [
          "Today Detected Flights",
          dashboardData.todayFlights ?? 0,
        ],
        [
          "Realtime Detected Flights",
          dashboardData.currFlightCount ?? 0,
        ],
        [
          "Today Important Flights",
          dashboardData.importantFlightCount ?? 0,
        ],
        [
          "Total Units",
          dashboardData.totalUnits ?? 0,
        ],
        [
          "Total Outposts",
          dashboardData.outpostCount ?? 0,
        ],
      ],
      theme: "grid",
      styles: {
        fontSize: 9,
      },
      headStyles: {
        fillColor: [54, 63, 92],
      },
    });

    // --------------------------------------------------
    // Users by Role
    // --------------------------------------------------

    let currentY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Users by Role", 14, currentY);

    autoTable(doc, {
      startY: currentY + 5,
      head: [["Role", "Users", "Percentage"]],
      body: (dashboardData.usersByRole || []).map((item) => {
        const percentage =
          dashboardData.totalRegUsers > 0
            ? Math.round(
                (item.value / dashboardData.totalRegUsers) * 100
              )
            : 0;

        return [
          item.label,
          item.value,
          `${percentage}%`,
        ];
      }),
      theme: "grid",
      styles: {
        fontSize: 9,
      },
      headStyles: {
        fillColor: [54, 63, 92],
      },
    });

    // --------------------------------------------------
    // Users by Online Status
    // --------------------------------------------------

    currentY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Users by Online Status", 14, currentY);

    autoTable(doc, {
      startY: currentY + 5,
      head: [["Status", "Users", "Percentage"]],
      body: (dashboardData.userStatesByOnline || []).map((item) => {
        const percentage =
          dashboardData.totalRegUsers > 0
            ? Math.round(
                (item.value / dashboardData.totalRegUsers) * 100
              )
            : 0;

        return [
          item.label,
          item.value,
          `${percentage}%`,
        ];
      }),
      theme: "grid",
      styles: {
        fontSize: 9,
      },
      headStyles: {
        fillColor: [54, 63, 92],
      },
    });

    // --------------------------------------------------
    // Outposts by Online Status
    // --------------------------------------------------

    currentY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Outposts by Online Status", 14, currentY);

    autoTable(doc, {
      startY: currentY + 5,
      head: [["Status", "Outposts", "Percentage"]],
      body: (dashboardData.outpostStatesByOnline || []).map((item) => {
        const percentage =
          dashboardData.outpostCount > 0
            ? Math.round(
                (item.value / dashboardData.outpostCount) * 100
              )
            : 0;

        return [
          item.label,
          item.value,
          `${percentage}%`,
        ];
      }),
      theme: "grid",
      styles: {
        fontSize: 9,
      },
      headStyles: {
        fillColor: [54, 63, 92],
      },
    });

    // --------------------------------------------------
    // Units by Type
    // --------------------------------------------------

    currentY = doc.lastAutoTable.finalY + 12;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Units by Type", 14, currentY);

    autoTable(doc, {
      startY: currentY + 5,
      head: [["Unit Type", "Units", "Percentage"]],
      body: (dashboardData.unitsByType || []).map((item) => {
        const percentage =
          dashboardData.totalUnits > 0
            ? Math.round(
                (item.value / dashboardData.totalUnits) * 100
              )
            : 0;

        return [
          item.label,
          item.value,
          `${percentage}%`,
        ];
      }),
      theme: "grid",
      styles: {
        fontSize: 9,
      },
      headStyles: {
        fillColor: [54, 63, 92],
      },
    });

    // --------------------------------------------------
    // Footer on every page
    // --------------------------------------------------

    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      doc.text(
        `Dashboard Report - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        {
          align: "center",
        }
      );
    }

    // --------------------------------------------------
    // Open PDF in new tab
    // --------------------------------------------------

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    window.open(pdfUrl, "_blank");

    // Release URL later
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 60000);
  };

  return (
    <>
      {auth && (
        <Box sx={pageSxFor(isNonMediumScreen)}>
          <FlexBetween
            sx={{
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              alignItems: {
                xs: 'flex-start',
                sm: 'center',
              },
              gap: '1rem',
            }}
          >
            {/* Header */}
            <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
            <PageBanner />

            {/* Content */}
            <Box>
              {/* Download Reports */}
              <Button
                onClick={handleDownloadReports}
                sx={{
                  backgroundColor: theme.palette.secondary.light,
                  color: theme.palette.background.alt,
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: theme.palette.background.alt,
                    color: theme.palette.secondary.light,
                  },
                }}
              >
                <DownloadOutlined sx={{ mr: "10px" }} />
                Download Reports
              </Button>
            </Box>
          </FlexBetween>

          <Box
            mt="10px"
            display="grid"
            gridTemplateColumns="repeat(8, 1fr)"
            gap="20px"
            sx={{
              // Rows grow to fill tall screens, never shrink below the old 170px
              flex: isNonMediumScreen ? 1 : "none",
              gridAutoRows: isNonMediumScreen ? "minmax(170px, 1fr)" : "170px",
              "& > div": {
                gridColumn: isNonMediumScreen ? undefined : "span 12",
              },
            }}
          >
            {/* ROW 1 */}   
            {/* Total Registered Users */}
            <StatBox
              title="Registered Users"
              value={dashboardData.totalRegUsers || 0}
              increase=""
              description="All accounts · chart: new per day, 15 days"
              unit="new users"
              chartData={dashboardData.totalRegUsersList}
              dataKey={'totalreguser_count'}
              icon={
                <PersonIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Today Logined Users */}
            <StatBox
              title="Users Logged In Today"
              value={dashboardData.todayLoginUsers}
              increase=""
              description="Unique users · chart: per day, 15 days"
              unit="users logged in"
              chartData={dashboardData.todayLoginUsersList}
              dataKey={'loginuser_count'}
              icon={
                <PersonIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Online Users By Hour */}
            <StatBox
              title="Users Online Now"
              value={dashboardData.currConnectionCount}
              increase=""
              description="Chart: by hour of last activity, today"
              unit="online users"
              chartData={dashboardData.currConnectionCountList}
              dataKey={'conn_count'}
              icon={
                <PersonIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Strange Users */}
            <StatBox
              title="Failed Logins"
              value={dashboardData.strangeUserCount}
              increase=""
              description="Last 24 hours · chart: per day, 15 days"
              unit="failed logins"
              chartData={dashboardData.strangeUserCountList}
              dataKey={'strangeuser_count'}
              icon={
                <PersonIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            <Box
              gridColumn="span 3"
              gridRow="span 2"
              backgroundColor={theme.palette.background.alt}
              p="1.25rem 1.5rem"
              borderRadius="0.55rem"
              sx={{ minWidth: 0 }}
            >
              <BreakdownChart
                title="Users by Role"
                icon={<BadgeOutlined />}
                unit="users"
                icons={ROLE_ICONS}
                data={dashboardData.usersByRole}
                total={dashboardData.totalRegUsers}
              />          
            </Box>

            <Box
              gridColumn="span 2"
              gridRow="span 2"
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              gridTemplateRows="repeat(2, minmax(0, 1fr))"
              gap="20px"
            >
               <Box
                gridColumn="span 2"
                gridRow="span 1"
                backgroundColor={theme.palette.background.alt}
                p="1.25rem 1.5rem"
                borderRadius="0.55rem"
              >
                <OnlineStateChart
                  title="Users by Online"
                  icon={<PeopleAltOutlined />}
                  unit="users"
                  data={dashboardData.userOnlineStatus}
                  total={dashboardData.totalRegUsers}
                />
              </Box>

              <Box
                gridColumn="span 2"
                gridRow="span 1"
                backgroundColor={theme.palette.background.alt}
                p="1.25rem 1.5rem"
                borderRadius="0.55rem"
              >
                <OnlineStateChart
                  title="Outposts by Online"
                  icon={<PlaceOutlined />}
                  unit="outposts"
                  data={dashboardData.outpostOnlineStatus}
                  total={dashboardData.outpostCount}
                />
              </Box>
              
            </Box>

            <Box
              gridColumn="span 3"
              gridRow="span 2"
              backgroundColor={theme.palette.background.alt}
              p="1.25rem 1.5rem"
              borderRadius="0.55rem"
              sx={{ minWidth: 0 }}
            >
              <BreakdownChart
                title="Units by Type"
                icon={<AccountTree />}
                unit="units"
                icons={UNIT_ICONS}
                data={dashboardData.unitsByType}
                total={dashboardData.totalUnits}
              /> 
            </Box>             

            {/* ROW 2 */}
            {/* Total Detected Flights */}
            <StatBox
              title="Total Detected Flights"
              value={dashboardData.totalFlights}
              increase=""
              description="All time · chart: per day, 15 days"
              unit="flights"
              chartData={dashboardData.totalFlightsList}
              dataKey={'totalflight_count'}
              icon={
                <FlightIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Today Detected Flights */}
            <StatBox
              title="Flights Detected Today"
              value={dashboardData.todayFlights}
              increase=""
              description="Since midnight · chart: per hour"
              unit="flights"
              chartData={dashboardData.detectedFlightsByHourList}
              dataKey={'todayflight_count'}
              icon={
                <FlightIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Realtime Detected Flights By Hour */}
            <StatBox
              title="Flights in the Last Hour"
              value={dashboardData.currFlightCount}
              increase=""
              description="Live · chart: per hour, 24 hours"
              unit="flights"
              chartData={dashboardData.currFlightCountList}
              dataKey={'currflight_count'}
              icon={
                <FlightIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Today Important Flights */}
            <StatBox
              title="Important Flights Today"
              value={dashboardData.importantFlightCount}
              increase=""
              description="Since midnight · chart: per day, 15 days"
              unit="important flights"
              chartData={dashboardData.importantFlightCountList}
              dataKey={'importantflight_count'}
              icon={
                <FlightIcon
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              }
              float={0}
            />

            {/* Overview Chart */}
            {dashboardData.hourDataList && dashboardData.hourDataList.length > 0 && (
              <Box
                gridColumn="span 8"
                gridRow="span 2"
                backgroundColor={theme.palette.background.alt}
                p="1.25rem"
                borderRadius="0.55rem"
                sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <HourStatisticChart data={dashboardData.hourDataList} />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default Dashboard